// ---------------------------------------------------------------------------
// The numbers step builder.
//
// Legality is decided by `validateStep` from @countdown/shared, the same
// function the server runs, so the client blocks exactly what the server would
// reject and for the same reason. A player told their step was fine and then
// told otherwise at the reveal has lost the round to us, not to the game.
//
// The builder never says whether the player is right. No distance to the target,
// no highlight when a step happens to land on it, no hint at a solution.
// Players read their own working.
//
// Two input surfaces, one representation. Tapping appends one step at a time.
// Typing goes through shared's own `parseExpression`, which turns
// `((75+25)*3)+7` into the same three steps tapping would have made and runs
// every one of them through `validateStep` on the way. Neither surface has a
// rule of its own, and the server cannot tell which one an answer came from.
//
// `parseExpression` parses a whole answer from the six tiles: it has no syntax
// for referring to an earlier step and it always numbers from A. So typing
// replaces the working list rather than extending it. That asymmetry is the one
// seam between the two surfaces, and the screen says so before the player
// commits to it rather than after.
//
// **A committed step seeds the next one as its left operand.** `10 x 2 x 3` used
// to cost eight taps, and the middle of it was hunting the pool for the result
// chip you had just made. It is six now, because the result is already sitting
// in the next step. The seed is marked `carried`, and while it is carried and no
// operator has been chosen the next value tapped REPLACES it rather than
// becoming the right operand — so two independent branches, `25 x 5` then
// `7 x 3`, cost exactly what they cost before. Choosing an operator is what
// settles the left operand; after that a tap fills the right, as it always did.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NUMBERS_MAX_STEPS,
  consumedOperands,
  nextStepId,
  parseExpression,
  resolveOperand,
  stepsToExpression,
  validateStep,
  validateStepList,
} from '@countdown/shared';
import type {
  NumbersOperand,
  NumbersOperator,
  NumbersStep,
  NumbersStepError,
} from '@countdown/shared';

export interface PendingStep {
  left: NumbersOperand | null;
  op: NumbersOperator | null;
  right: NumbersOperand | null;
  /**
   * The left operand was seeded by the step just committed, not chosen. While
   * it is carried and no operator has been picked, the next value selected
   * replaces it. The screen has to draw a carried operand differently from a
   * chosen one, or the escape hatch is invisible and the chain becomes a trap.
   */
  carried: boolean;
}

export interface NumbersBuilderApi {
  steps: NumbersStep[];
  pending: PendingStep;
  /** The declared answer: the value of the last committed step. */
  value: number | null;
  selectOperand: (operand: NumbersOperand) => void;
  selectOperator: (op: NumbersOperator) => void;
  commit: () => void;
  clearPending: () => void;
  undoLast: () => void;
  reset: () => void;
  /**
   * Adopt a step list the player already committed — their own answer, coming
   * back from the server after a reconnect. Without it a player who dropped
   * mid-round returns to an empty working list and cannot read back what they
   * built.
   *
   * **It restores at most once per mount, and "the list is empty" is not the
   * test.** The screen calls this on every change to its own submission, so a
   * broadcast is a chance to hydrate on every one of them — and the broadcast
   * that answers `Start over` was built before the empty draft arrived, so it
   * still carries the steps that were just cleared. Gated on emptiness alone,
   * that echo passed the gate the reset had itself opened, and the working list
   * came back with the tiles under it dark again. Isaac reported it as a number
   * tile stuck spent after resetting the board.
   *
   * So the gate is `commitSteps`: any local commit, an empty one included,
   * closes it for good. A reset is a decision, not an absence of one, and the
   * only thing this is for is a client that has made no decisions at all.
   */
  hydrate: (steps: NumbersStep[]) => void;
  /**
   * The value the pending step would produce, or null when it is incomplete or
   * illegal. Computed here rather than in each screen so the number shown beside
   * `A × B` comes from the same validator the server runs.
   */
  pendingValue: number | null;
  /** Null when the pending step is legal or incomplete; a sentence when it is not. */
  pendingError: string | null;
  /** Why this operator is unavailable for the current pending operands, or null. */
  operatorBlockedReason: (op: NumbersOperator) => string | null;
  isConsumed: (operand: NumbersOperand) => boolean;
  /**
   * Adopt a typed expression as the whole working list. Returns null when it
   * was taken, or one sentence about what the player wrote when it was not —
   * in which case nothing changes, so a typo costs them their text and not
   * their working.
   */
  applyExpression: (expression: string) => string | null;
  /**
   * The working list as one expression, or null when it is not one sum: an
   * answer built as two unjoined halves has no single line that means it, and
   * showing one of the halves as though it were the whole would be a lie the
   * player would only catch at the reveal.
   */
  canonicalExpression: string | null;
}

export interface StepErrorContext {
  left?: number | null;
  op?: NumbersOperator | null;
  right?: number | null;
}

/**
 * A step error as a sentence, naming the numbers when they are known.
 *
 * Shared owns the error codes but keeps its own message table module-private,
 * and its sentences are generic where the handoff's name the operands: `83 / 9
 * is not a whole number` rather than "that division does not come out whole".
 * This is the only step-error vocabulary the frontend has — the typed surface
 * comes through it too, via `describeParseFailure` below — so a rule is worded
 * one way on this screen no matter which surface raised it.
 *
 * If shared ever exports a `describeStepError` of its own, delete this and use
 * it. Doing it the other way round is what created the two vocabularies: the
 * table in `packages/shared/src/numbers.ts` is module-private and cannot be
 * reached from here, so the choice was to duplicate it or to leave the typed
 * path speaking a second dialect.
 */
export function describeStepError(error: NumbersStepError, context?: StepErrorContext): string {
  const { left, op, right } = context ?? {};
  const sum =
    left !== null && left !== undefined && right !== null && right !== undefined && op
      ? `${left} ${op} ${right}`
      : null;

  switch (error) {
    case 'non-integer-division':
      return sum === null ? 'That division is not a whole number.' : `${sum} is not a whole number.`;
    case 'division-by-zero':
      return 'You cannot divide by zero.';
    case 'non-positive-result':
      return sum === null
        ? 'That would land at or below zero.'
        : `${sum} would land at or below zero.`;
    case 'operand-already-consumed':
      return 'That tile is already spent.';
    case 'unknown-operand':
      return 'That is not one of your tiles.';
    case 'too-many-steps':
      // Shared's own wording, kept to the letter: this one is raised by a typed
      // expression too, where "you have used all five" would be about steps the
      // player never took.
      return `You cannot use more than ${NUMBERS_MAX_STEPS} steps.`;
  }
}

/**
 * A failed parse as one sentence.
 *
 * One vocabulary for the six rules: every `NumbersStepError` a parse can raise
 * comes back through `describeStepError` above, so a rule is worded the same
 * whether the player tapped it or typed it. One screen saying both "that
 * division does not come out whole" and "that division is not a whole number"
 * reads as two different rules to a player under a clock.
 *
 * Two failures keep the parser's own words, because the parser knows something
 * the tap path cannot: `parse-error` is about the text (which bracket is open,
 * what is left over), and its `unknown-operand` quotes the literal that has no
 * tile — `4 is not one of your tiles.` rather than `That is not one of your
 * tiles.` Losing the number to say it in our words would be a worse sentence,
 * not a more consistent one.
 */
export function describeParseFailure(failure: {
  error: NumbersStepError | 'parse-error';
  message: string;
}): string {
  switch (failure.error) {
    case 'parse-error':
    case 'unknown-operand':
      return failure.message;
    default:
      return describeStepError(failure.error);
  }
}

/**
 * True when the steps join into a single sum: everything except the last is an
 * operand of something later. Two unjoined halves are legal working — only the
 * last step is the declared answer — but no one expression means them.
 */
function isOneSum(steps: NumbersStep[]): boolean {
  if (steps.length === 0) return false;
  const { stepIds } = consumedOperands(steps);
  return steps.slice(0, -1).every((step) => stepIds.has(step.id));
}

const EMPTY_PENDING: PendingStep = { left: null, op: null, right: null, carried: false };

function sameOperand(a: NumbersOperand | null, b: NumbersOperand | null): boolean {
  if (a === null || b === null) return false;
  if (a.kind === 'tile' && b.kind === 'tile') return a.tileIndex === b.tileIndex;
  if (a.kind === 'step' && b.kind === 'step') return a.stepId === b.stepId;
  return false;
}

export function useNumbersBuilder(
  tiles: number[],
  onChange: (steps: NumbersStep[]) => void,
): NumbersBuilderApi {
  const [steps, setSteps] = useState<NumbersStep[]>([]);
  const [pending, setPending] = useState<PendingStep>(EMPTY_PENDING);
  // Mirrors the committed steps so the event handlers stay stable and call
  // onChange themselves instead of hiding a side effect in a state updater.
  //
  // Event handlers only. Anything computed during render must read `steps`: a
  // ref does not participate in memo invalidation, so a validation memoized
  // against the ref keeps reporting last render's answer.
  const stepsRef = useRef<NumbersStep[]>([]);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Whether this player has decided anything about their working list yet. See
  // the note on `hydrate`: the server's copy may only be adopted before the
  // first decision, and clearing the list is a decision.
  const decidedRef = useRef(false);

  const commitSteps = useCallback((next: NumbersStep[]) => {
    decidedRef.current = true;
    stepsRef.current = next;
    setSteps(next);
    onChangeRef.current(next);
  }, []);

  const consumed = useMemo(() => consumedOperands(steps), [steps]);

  // Only committed steps spend an operand. A pending step can be cleared, so
  // the tile sitting in it is claimed, not spent.
  const isConsumed = useCallback(
    (operand: NumbersOperand) =>
      operand.kind === 'tile'
        ? consumed.tileIndices.has(operand.tileIndex)
        : consumed.stepIds.has(operand.stepId),
    [consumed],
  );

  const selectOperand = useCallback(
    (operand: NumbersOperand) => {
      if (isConsumed(operand)) return;
      if (resolveOperand(operand, tiles, steps) === null) return;
      setPending((current) => {
        // A carried left is a suggestion, not a choice. Until an operator
        // settles it, the next value tapped takes its place — otherwise a
        // player starting a second, unrelated branch would have to clear the
        // step the chain seeded for them before they could begin.
        if (current.carried && current.op === null) {
          return { left: operand, op: null, right: null, carried: false };
        }
        if (current.left === null) return { ...current, left: operand };
        // Tapping the operand already on the left takes it back off, which is
        // the only undo a half-built step needs.
        if (sameOperand(current.left, operand) && current.right === null) {
          return { ...current, left: null };
        }
        if (sameOperand(current.right, operand)) return { ...current, right: null };
        return { ...current, right: operand };
      });
    },
    [isConsumed, tiles, steps],
  );

  // Choosing an operator is what settles the left operand: a carried seed stops
  // being replaceable here, and the screen stops advertising that it is.
  const selectOperator = useCallback((op: NumbersOperator) => {
    setPending((current) =>
      current.left === null ? current : { ...current, op, carried: false },
    );
  }, []);

  /** Validate a fully populated candidate, or null when there is nothing to judge. */
  const judge = useCallback(
    (candidate: PendingStep): string | null => {
      const { left, op, right } = candidate;
      if (left === null || op === null || right === null) return null;
      const check = validateStep(tiles, steps, { left, right, op });
      if (check.ok) return null;
      return describeStepError(check.error, {
        left: resolveOperand(left, tiles, steps),
        op,
        right: resolveOperand(right, tiles, steps),
      });
    },
    [tiles, steps],
  );

  const pendingError = useMemo(() => judge(pending), [judge, pending]);

  const operatorBlockedReason = useCallback(
    (op: NumbersOperator) => judge({ ...pending, op }),
    [judge, pending],
  );

  const commit = useCallback(() => {
    const { left, op, right } = pending;
    if (left === null || op === null || right === null) return;
    const check = validateStep(tiles, stepsRef.current, { left, right, op });
    if (!check.ok) return;
    const step: NumbersStep = {
      id: nextStepId(stepsRef.current),
      left,
      right,
      op,
      result: check.result,
    };
    const next = [...stepsRef.current, step];
    commitSteps(next);
    // The result carries forward as the next step's left operand. Wiping the
    // pending step instead is what made `10 x 2 x 3` eight taps, four of them
    // spent finding a chip the player had just made. At the ceiling there is no
    // next step to seed, so nothing is claimed that cannot be used.
    setPending(
      next.length >= NUMBERS_MAX_STEPS
        ? EMPTY_PENDING
        : { left: { kind: 'step', stepId: step.id }, op: null, right: null, carried: true },
    );
  }, [commitSteps, pending, tiles]);

  const clearPending = useCallback(() => setPending(EMPTY_PENDING), []);

  const applyExpression = useCallback(
    (expression: string): string | null => {
      // Shared's parser, not one of ours: it runs every step it builds through
      // the same `validateStep` the tap path and the server run, so a typed
      // answer is blocked for exactly the reasons a tapped one would be.
      const parsed = parseExpression(tiles, expression);
      if (!parsed.ok) return describeParseFailure(parsed);
      // The half-built tap step is dropped with the list it was built against.
      // Its operands may be spent by the new working, and a pending step that
      // silently became illegal is worse than one that is visibly gone.
      setPending(EMPTY_PENDING);
      commitSteps(parsed.steps);
      return null;
    },
    [commitSteps, tiles],
  );

  const undoLast = useCallback(() => {
    if (stepsRef.current.length === 0) return;
    // The pending step may reference the step being removed, so it goes too.
    setPending(EMPTY_PENDING);
    commitSteps(stepsRef.current.slice(0, -1));
  }, [commitSteps]);

  const reset = useCallback(() => {
    setPending(EMPTY_PENDING);
    commitSteps([]);
  }, [commitSteps]);

  const hydrate = useCallback((incoming: NumbersStep[]) => {
    // Local working always wins, and so does a local decision to have none.
    // The server copy is only ever a fallback for a client that lost its own,
    // so adopting it over live input — or over a list the player has just
    // emptied on purpose — would be a regression dressed up as a recovery.
    if (decidedRef.current || stepsRef.current.length > 0 || incoming.length === 0) return;
    decidedRef.current = true;
    stepsRef.current = incoming;
    setSteps(incoming);
    // Deliberately no onChange: this is the server's own state coming home, and
    // echoing it back as a fresh draft would be a pointless round trip.
  }, []);

  // Recomputed through the shared validator rather than read off the last step,
  // so the value the player declares is the one the server will recompute.
  const value = useMemo(() => {
    if (steps.length === 0) return null;
    const check = validateStepList(tiles, steps);
    return check.ok ? check.value : null;
  }, [steps, tiles]);

  // The same validator, applied to the half-built step, so the preview beside
  // `A × B` cannot disagree with what committing it would produce.
  const pendingValue = useMemo(() => {
    const { left, op, right } = pending;
    if (left === null || op === null || right === null) return null;
    const check = validateStep(tiles, steps, { left, right, op });
    return check.ok ? check.result : null;
  }, [pending, steps, tiles]);

  // Rendered by shared's own renderer, so what the field shows parses back to
  // the working list it came from.
  const canonicalExpression = useMemo(
    () => (isOneSum(steps) ? stepsToExpression(tiles, steps) : null),
    [steps, tiles],
  );

  return {
    steps,
    pending,
    value,
    pendingValue,
    hydrate,
    applyExpression,
    canonicalExpression,
    selectOperand,
    selectOperator,
    commit,
    clearPending,
    undoLast,
    reset,
    pendingError,
    operatorBlockedReason,
    isConsumed,
  };
}
